import React from 'react';
import ReactPlayer from 'react-player';
import TalkingPoint from '../components/Home/TalkingPoint';
import {Container, styled, useTheme, useMediaQuery, Box, Grid} from '@mui/material';
import backgroundHomeSm from '../assets/background-home-sm.png';
import backgroundHomeLg from '../assets/background-home-lg.png';
import useGetTalkingPoints from '../hooks/useGetTalkingPoints';
import CurrentTalkingPoint from '../components/Home/CurrentTalkingPoint';
import TalkingPointsStack from '../components/Home/TalkingPointsStack';
import {useBoundingclientrectRef, useOnWindowResize} from 'rooks';
import {useVoting} from '../providers/voting';
import SimpleBar from 'simplebar-react';
import {SystemProps} from '@mui/system';
import ActionsSpeedDial from '../components/Home/ActionsSpeedDial';

const ResponsiveReactPlayer = styled(ReactPlayer)({
  position: 'absolute',
  top: 0,
  left: 0,
  border: '1px solid #0000001F',
  background: 'rgba(255, 255, 255, 0.3)',
  backdropFilter: 'blur(5px)',
  borderRadius: '12px',
  overflow: 'hidden',
});

const HomeContainer = styled('div')(({theme}) => ({
  backgroundColor: '#ecedf9',
  backgroundImage: `url(${backgroundHomeSm})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  minHeight: '100vh',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    backgroundImage: `url(${backgroundHomeLg})`,
  },
}));

const VideoContainer = styled('div')(({theme}) => ({
  position: 'fixed',
  top: 0,
  left: 8,
  right: 8,
  zIndex: 1,
  paddingTop: '60%',
  '& video': {
    objectFit: 'cover',
  },
  [`${theme.breakpoints.down('md')} and (min-aspect-ratio: 1/1)`]: {
    position: 'relative',
    left: 'unset',
    right: 'unset',
    top: 'unset',
    height: '350px',
    paddingTop: 0,
  },
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    left: 'unset',
    right: 'unset',
    top: 'unset',
    height: '400px',
    paddingTop: 0,
  },
}));

const TalkingPointGridItem = styled(Grid)(({theme}) => ({
  marginTop: '60%',
  width: '100%',
  zIndex: 0,
  [`${theme.breakpoints.down('md')} and (min-aspect-ratio: 1/1)`]: {
    marginTop: 0,
  },
  [theme.breakpoints.up('md')]: {
    marginTop: 0,
  },
}));

const TPSimplestBar = styled(SimpleBar)(({theme}) => ({
  maxHeight: '100vh',
  paddingRight: 16,
  '& .simplebar-track': {
    backgroundColor: '#ecedf9',
    boxShadow: 'inset 0 0 6px #ecedf955',
    '& .simplebar-scrollbar::before': {
      background: '#323232',
    },
  },
}));

function TalkingPointScrollbarContainer({children}: {children: React.ReactElement}): JSX.Element {
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('md'));
  if (!isBigScreen) {
    return children;
  }

  return (
    <TPSimplestBar forceVisible="y" autoHide={false}>
      {children}
    </TPSimplestBar>
  );
}

const talkingPointsTimestamp = [
  {
    id: '1',
    // in seconds
    timestamp: {
      start: 0,
      end: 10,
    },
  },
  {
    id: '2',
    timestamp: {
      start: 10.1,
      end: 20,
    },
  },
  {
    id: '3',
    timestamp: {
      start: 20.1,
      end: 30,
    },
  },
  {
    id: '4',
    timestamp: {
      start: 30.1,
      end: Infinity,
    },
  },
];

function Home() {
  const getTalkingPointsResult = useGetTalkingPoints();
  const {currentTalkingPointId, changeCurrentTalkingPointId} = useVoting();
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [myRef, boundingClientRect, updateRect] = useBoundingclientrectRef();
  const [myItemRef, itemBoundingClientRect, updateItemRect] = useBoundingclientrectRef();

  const currentTalkingPoint = getTalkingPointsResult?.data?.find(
    talkingPoint => talkingPoint.id === currentTalkingPointId
  );

  useOnWindowResize(() => {
    updateRect();
    if (isBigScreen) {
      updateItemRect();
    }
  });

  const spacing = 16;

  const fixedTalkingPoints: SystemProps = {
    position: 'fixed',
    left: `${(itemBoundingClientRect?.left ?? 0) + spacing}px`,
    width: `${(itemBoundingClientRect?.width ?? 0) - spacing}px`,
  };

  const skeleton =
    !getTalkingPointsResult?.data?.length &&
    (getTalkingPointsResult?.isValidating || !!getTalkingPointsResult?.error);

  return (
    <HomeContainer>
      <Container
        disableGutters
        sx={{
          px: {xs: '8px', md: 2},
        }}
      >
        <ActionsSpeedDial />
        <Grid
          container
          spacing={{
            sx: '0',
            md: 2,
          }}
        >
          <Grid item md={7} sx={{zIndex: {md: 1201}, position: 'relative', width: '100%'}}>
            <VideoContainer sx={{mt: {md: 4}}}>
              <ResponsiveReactPlayer
                width="100%"
                height="100%"
                muted
                controls
                onProgress={({playedSeconds}) => {
                  const nextTpId = talkingPointsTimestamp.find(
                    tp => playedSeconds >= tp.timestamp.start && playedSeconds < tp.timestamp.end
                  )?.id;

                  if (nextTpId && nextTpId !== currentTalkingPointId) {
                    changeCurrentTalkingPointId(nextTpId);
                  }
                }}
                url="https://youtu.be/2Gg6Seob5Mg"
              />
            </VideoContainer>

            {isBigScreen && (
              <Box sx={{my: {md: 2}}}>
                <CurrentTalkingPoint
                  data={currentTalkingPoint}
                  PopupProps={{
                    hideBackdrop: !isBigScreen,
                    paperCoords: {
                      left: boundingClientRect?.left || 0,
                      width: boundingClientRect?.width || 200,
                    },
                  }}
                  skeleton={skeleton || !currentTalkingPoint}
                />
              </Box>
            )}
          </Grid>
          <TalkingPointGridItem ref={myItemRef} item md={5}>
            <Box sx={{...(isBigScreen ? fixedTalkingPoints : {})}}>
              <TalkingPointScrollbarContainer>
                <>
                  {!skeleton && (
                    <TalkingPointsStack ref={myRef} spacing={7}>
                      {getTalkingPointsResult?.data?.map(talkingPoint => (
                        <TalkingPoint
                          key={talkingPoint.id}
                          data={talkingPoint}
                          active={talkingPoint.id === currentTalkingPointId}
                          PopupProps={{
                            hideBackdrop: !isBigScreen,
                            paperCoords: {
                              left: boundingClientRect?.left || 0,
                              width: boundingClientRect?.width || 200,
                            },
                          }}
                        />
                      ))}
                    </TalkingPointsStack>
                  )}
                  {skeleton && (
                    <TalkingPointsStack spacing={7}>
                      <TalkingPoint skeleton />
                      <TalkingPoint skeleton />
                      <TalkingPoint skeleton />
                    </TalkingPointsStack>
                  )}
                </>
              </TalkingPointScrollbarContainer>
            </Box>
          </TalkingPointGridItem>
        </Grid>
      </Container>
    </HomeContainer>
  );
}

export default Home;
